import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface WeeklyData {
  week: string;
  count?: number;
  avg_score?: number;
  total?: number;
  successful?: number;
  failed?: number;
  uncertain?: number;
  pending?: number;
}

export interface InsightsSummary {
  total_matches: number;
  total_applications: number;
  successful_applications: number;
  failed_applications: number;
  uncertain_applications: number;
  avg_match_score: number;
  booked_gigs: number;
  tentative_schedule_holds: number;
}

export interface SourceData {
  source: string;
  match_count: number;
  avg_score: number;
}

export interface InsightsState {
  weekly_matches: WeeklyData[];
  weekly_applications: WeeklyData[];
  summary: InsightsSummary | null;
  top_sources: SourceData[];
  loading: boolean;
  error: string | null;
}

const initialState: InsightsState = {
  weekly_matches: [],
  weekly_applications: [],
  summary: null,
  top_sources: [],
  loading: false,
  error: null,
};

export const fetchInsights = createAsyncThunk(
  'insights/fetchInsights',
  async (weeks: number | undefined, { rejectWithValue }) => {
    try {
      const params = weeks ? `?weeks=${weeks}` : '';
      const response = await api.get(`/insights/weekly${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch insights');
      }
      return rejectWithValue('Failed to fetch insights');
    }
  }
);

const insightsSlice = createSlice({
  name: 'insights',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInsights.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInsights.fulfilled, (state, action) => {
        state.loading = false;
        state.weekly_matches = action.payload.weekly_matches;
        state.weekly_applications = action.payload.weekly_applications;
        state.summary = action.payload.summary;
        state.top_sources = action.payload.top_sources;
      })
      .addCase(fetchInsights.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default insightsSlice.reducer;
